import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';
import CompanyLogo from '../components/CompanyLogo';
import CompanyTimezoneCard from '../components/CompanyTimezoneCard';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import { SmtpTestPanel } from '../components/SmtpTestPanel';
import {
  Palette, Upload, Trash2, Loader2, Save, Building2, Mail, Bell, FileText, Image as ImageIcon, Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'branding', label: 'Branding', icon: Palette, testid: 'tab-branding' },
  { id: 'business', label: 'Business details', icon: Building2, testid: 'tab-business' },
  { id: 'email', label: 'Email / SMTP', icon: Mail, testid: 'tab-email' },
  { id: 'notifications', label: 'Notifications', icon: Bell, testid: 'tab-notifications' },
  { id: 'templates', label: 'Document templates', icon: FileText, testid: 'tab-templates' },
];

const ColorInput = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {label}
    </label>
    <div className="flex items-center gap-2">
      <input
        type="color"
        data-testid={`${testid}-picker`}
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        className="w-12 h-10 rounded-md cursor-pointer border border-slate-300"
      />
      <input
        type="text"
        data-testid={`${testid}-hex`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#002FA7"
        className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      />
    </div>
  </div>
);

const TextField = ({ label, value, onChange, placeholder, testid, type = 'text', textarea = false, rows = 3, maxLength }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {label}
    </label>
    {textarea ? (
      <textarea
        data-testid={testid}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      />
    ) : (
      <input
        type={type}
        data-testid={testid}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      />
    )}
  </div>
);

const Toggle = ({ label, hint, value, onChange, testid }) => (
  <label className="flex items-start justify-between gap-3 py-3 cursor-pointer border-b border-slate-100 last:border-b-0" data-testid={testid}>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{label}</p>
      {hint && <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{hint}</p>}
    </div>
    <button
      type="button"
      onClick={() => onChange(!value)}
      data-testid={`${testid}-button`}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${value ? 'bg-[#002FA7]' : 'bg-slate-300'}`}
    >
      <span className={`inline-block h-5 w-5 rounded-full bg-white shadow translate-y-0.5 transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
    </button>
  </label>
);

const CompanyBranding = () => {
  const { branding, refresh } = useBranding();
  const { user } = useAuth();
  const [tab, setTab] = useState('branding');
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    setForm(branding || {});
  }, [branding]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      // Strip readonly server fields
      const payload = { ...form };
      delete payload.logo_url;
      delete payload.logo_updated_at;
      delete payload.is_default;
      delete payload.has_smtp_password;
      ['smtp_status', 'smtp_last_test_at', 'smtp_last_test_ok', 'smtp_last_test_to', 'smtp_last_test_error', 'smtp_verified_at'].forEach((k) => delete payload[k]);
      // If smtp_password is the masked placeholder "(set)" — don't send it
      if (payload.smtp_password === '__keep__') delete payload.smtp_password;
      await axios.put(`${API}/company/branding`, payload, { withCredentials: true });
      toast.success('Branding updated');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const onLogoChange = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error('Logo too large (max 3MB)');
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/company/logo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo updated — applied across the portal');
      await refresh();
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!window.confirm('Remove the company logo?')) return;
    try {
      await axios.delete(`${API}/company/logo`, { withCredentials: true });
      toast.success('Logo removed');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="company-branding-page">
        <div className="mb-8 flex items-start gap-3">
          <div className="w-11 h-11 rounded-md flex items-center justify-center" style={{ background: 'var(--brand-primary, #002FA7)' }}>
            <Palette className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Company branding
            </h1>
            <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Make CivilX TMS feel like your own. Changes apply instantly across the portal for every user in your company.
            </p>
          </div>
        </div>

        <CompanyTimezoneCard />

        {/* Tabs */}
        <div className="bg-white border border-slate-200 rounded-md mb-6 overflow-hidden">
          <div className="flex overflow-x-auto border-b border-slate-100">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={t.testid}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${active ? 'border-[#002FA7] text-[#002FA7] font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                  style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="p-6 space-y-6">
            {tab === 'branding' && (
              <>
                {/* Logo upload */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Logo</p>
                    <div className="border border-slate-200 rounded-md p-6 bg-slate-50 flex flex-col items-center" data-testid="logo-preview">
                      <CompanyLogo size={16} showName={false} testid="logo-preview-image" />
                      <p className="text-sm text-slate-700 mt-3 font-semibold" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        {branding.company_name || 'Your company'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <input
                        ref={fileRef}
                        type="file"
                        data-testid="logo-file-input"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={(e) => onLogoChange(e.target.files?.[0])}
                        className="text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#002FA7] file:text-white file:font-semibold file:hover:bg-[#00247D]"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        disabled={uploading}
                      />
                      {uploading && <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />}
                      {branding.logo_url && !uploading && (
                        <button
                          type="button"
                          onClick={removeLogo}
                          data-testid="logo-remove"
                          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      PNG, JPG, WEBP or SVG up to 3MB. Displayed on sidebar, dashboards and PDF exports.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <ColorInput label="Primary brand color" value={form.primary_color} onChange={(v) => set('primary_color', v)} testid="primary-color" />
                    <ColorInput label="Sidebar color" value={form.sidebar_color} onChange={(v) => set('sidebar_color', v)} testid="sidebar-color" />
                    <ColorInput label="Navbar color" value={form.navbar_color} onChange={(v) => set('navbar_color', v)} testid="navbar-color" />
                    <ColorInput label="Accent color" value={form.accent_color} onChange={(v) => set('accent_color', v)} testid="accent-color" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      App theme
                    </label>
                    <div className="flex gap-2">
                      {['light', 'dark'].map((t) => (
                        <button
                          key={t}
                          type="button"
                          data-testid={`theme-${t}`}
                          onClick={() => set('theme', t)}
                          className={`flex-1 px-3 py-2 border rounded-md text-sm capitalize transition-colors ${form.theme === t ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7] font-semibold' : 'border-slate-300 text-slate-700 hover:border-slate-400'}`}
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                        >
                          {form.theme === t && <Check className="w-3.5 h-3.5 inline mr-1" />}
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <TextField
                    label="Dashboard welcome message"
                    value={form.welcome_message}
                    onChange={(v) => set('welcome_message', v)}
                    placeholder="Welcome to AssetCo Fleet Hub"
                    testid="welcome-message"
                    maxLength={200}
                  />
                </div>
              </>
            )}

            {tab === 'business' && (
              <>
                {(() => {
                  const required = ['address_line1', 'city', 'state', 'zip_code', 'phone'];
                  const missing = required.filter((k) => !form[k]);
                  return missing.length === 0 ? (
                    <div
                      data-testid="business-details-complete-chip"
                      className="mb-4 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-2 text-xs font-semibold"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <Check className="w-4 h-4" strokeWidth={2.5} />
                      Business details complete — invoices &amp; paystubs will use this address.
                    </div>
                  ) : (
                    <div
                      data-testid="business-details-incomplete-chip"
                      className="mb-4 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-xs font-semibold"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      <span className="w-4 h-4 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px]">!</span>
                      {missing.length} field{missing.length === 1 ? '' : 's'} still needed for invoices &amp; paystubs.
                    </div>
                  );
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField label="Company name (display)" value={form.company_name} onChange={(v) => set('company_name', v)} placeholder="AssetCo Fleet" testid="company-name" maxLength={120} />
                <TextField label="Legal company name" value={form.legal_name} onChange={(v) => set('legal_name', v)} placeholder="AssetCo Logistics LLC" testid="legal-name" />
                <TextField label="DOT number" value={form.dot_number} onChange={(v) => set('dot_number', v)} placeholder="1234567" testid="dot-number" />
                <TextField label="MC number" value={form.mc_number} onChange={(v) => set('mc_number', v)} placeholder="MC-7654321" testid="mc-number" />
                <TextField label="Phone" value={form.phone} onChange={(v) => set('phone', v)} placeholder="+1 (214) 555-0100" testid="phone" />
                <TextField label="Support email" value={form.support_email} onChange={(v) => set('support_email', v)} placeholder="support@assetco.com" testid="support-email" />
                <TextField label="Website" value={form.website} onChange={(v) => set('website', v)} placeholder="https://assetco.com" testid="website" />
                <TextField label="Address line 1" value={form.address_line1} onChange={(v) => set('address_line1', v)} placeholder="100 Logistics Way" testid="address-line1" />
                <TextField label="Address line 2" value={form.address_line2} onChange={(v) => set('address_line2', v)} placeholder="Suite 200" testid="address-line2" />
                <TextField label="City" value={form.city} onChange={(v) => set('city', v)} placeholder="Dallas" testid="city" />
                <TextField label="State / Province" value={form.state} onChange={(v) => set('state', v)} placeholder="TX" testid="state" />
                <TextField label="ZIP / Postal code" value={form.zip_code} onChange={(v) => set('zip_code', v)} placeholder="75201" testid="zip-code" />
                <TextField label="Country" value={form.country} onChange={(v) => set('country', v)} placeholder="USA" testid="country" />
                </div>
              </>
            )}

            {tab === 'email' && (
              <div className="space-y-5">
                <SmtpTestPanel branding={branding} defaultRecipient={user?.email} onTested={refresh} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField label="SMTP host" value={form.smtp_host} onChange={(v) => set('smtp_host', v)} placeholder="smtp.gmail.com" testid="smtp-host" />
                  <TextField label="SMTP port" type="number" value={form.smtp_port} onChange={(v) => set('smtp_port', v)} placeholder="587" testid="smtp-port" />
                  <TextField label="SMTP username" value={form.smtp_user} onChange={(v) => set('smtp_user', v)} placeholder="alerts@assetco.com" testid="smtp-user" />
                  <TextField
                    label={form.has_smtp_password ? 'SMTP password (replace, or leave blank to keep)' : 'SMTP password'}
                    value={form.smtp_password}
                    onChange={(v) => set('smtp_password', v)}
                    placeholder={form.has_smtp_password ? '••••••••' : 'app password'}
                    testid="smtp-password"
                    type="password"
                  />
                  <TextField label="From name" value={form.smtp_from_name} onChange={(v) => set('smtp_from_name', v)} placeholder="AssetCo Notifications" testid="smtp-from-name" />
                  <TextField label="From email" value={form.smtp_from_email} onChange={(v) => set('smtp_from_email', v)} placeholder="alerts@assetco.com" testid="smtp-from-email" />
                </div>
                <Toggle label="Use TLS (required)" hint="Encrypted connections only: port 465 uses implicit TLS; any other port must support STARTTLS. If this is off on a non-465 port, test e-mails are rejected. Server certificates are always verified." value={!!form.smtp_use_tls} onChange={(v) => set('smtp_use_tls', v)} testid="smtp-use-tls" />
                <p className="text-[11px] text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  The password is encrypted before it is stored and is never returned by the API. Phase 1 uses these settings for test e-mails only — paystubs, invoices, invitations and alerts continue to go through the platform mailer.
                </p>
              </div>
            )}

            {tab === 'notifications' && (
              <div className="divide-y divide-slate-100">
                <Toggle label="In-app notifications" hint="Show alerts in the notification bell." value={!!form.notify_in_app} onChange={(v) => set('notify_in_app', v)} testid="notify-in-app" />
                <Toggle label="Email notifications" hint="Mirror in-app alerts to your team's inboxes. Requires SMTP." value={!!form.notify_email} onChange={(v) => set('notify_email', v)} testid="notify-email" />
                <Toggle label="Load status changes" hint="Alert when loads change status (assigned → delivered)." value={!!form.notify_load_status_changes} onChange={(v) => set('notify_load_status_changes', v)} testid="notify-load-status" />
                <Toggle label="Document uploads" hint="Alert when drivers upload POD, BOL, or other docs." value={!!form.notify_document_uploads} onChange={(v) => set('notify_document_uploads', v)} testid="notify-doc-uploads" />
                <Toggle label="Subscription events" hint="Alert all users on past-due, suspension, and restoration." value={!!form.notify_subscription_events} onChange={(v) => set('notify_subscription_events', v)} testid="notify-subscription" />
              </div>
            )}

            {tab === 'templates' && (
              <div className="space-y-4">
                <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  Custom header / footer text appears on generated PDFs (paystubs, invoices, reports) along with your logo and address.
                </p>
                <TextField label="Invoice header" value={form.invoice_header} onChange={(v) => set('invoice_header', v)} placeholder="THANK YOU FOR YOUR BUSINESS" testid="invoice-header" textarea rows={2} />
                <TextField label="Invoice footer" value={form.invoice_footer} onChange={(v) => set('invoice_footer', v)} placeholder="Payment due net-30. Late fees apply per contract." testid="invoice-footer" textarea rows={3} />
                <TextField label="Paystub footer" value={form.paystub_footer} onChange={(v) => set('paystub_footer', v)} placeholder="Direct-deposit details on file. Contact HR for questions." testid="paystub-footer" textarea rows={3} />
                <TextField label="Report footer" value={form.report_footer} onChange={(v) => set('report_footer', v)} placeholder="Confidential — internal use only." testid="report-footer" textarea rows={2} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 bg-white border border-slate-200 rounded-md px-4 py-3" data-testid="branding-save-bar">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="branding-save"
            className="px-5 py-2.5 text-sm font-semibold text-white rounded-md disabled:opacity-50 flex items-center gap-2 hover:brightness-110 transition-all"
            style={{ background: 'var(--brand-primary, #002FA7)', fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-3 text-sm text-slate-600" data-testid="welcome-checklist-restore-bar">
          <span>Hid the "Get your fleet running" checklist on your dashboard?</span>
          <button type="button" onClick={async () => { try { await axios.post(`${API}/onboarding/checklist/restore`, {}, { withCredentials: true }); toast.success('Checklist restored on your dashboard'); } catch { toast.error('Could not restore checklist'); } }} className="font-semibold text-[#002FA7] hover:underline" data-testid="welcome-checklist-restore">Show it again</button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompanyBranding;
